<template>
  <b-modal  v-model="isOpen"
            size="lg"
            centered
            header-class="modal-header-full"
            footer-class="d-none"
            hide-header-close>
    <template #title>
      <div class="header-layout">
        <div class="header-left">
          <h2 class="modal-title">
            <strong>{{ editMode ? $t('Webportal.View.Edit') : $t('Webportal.View.Add') }}</strong>
          </h2>
          <p class="modal-description">
            {{ $t('Webportal.View.Edit.Description')}}
          </p>
        </div>
        <div class="header-actions">
          <b-button variant="outline-secondary" size="sm" @click="handleCancel()">
            {{ $t('Webportal.Dialog.Cancel')}}
          </b-button>
          <b-button v-if="editMode" variant="primary" size="sm" @click="handleOk()" :disabled="!viewName.trim() || !isNameUnique">
            {{ $t('Webportal.Save')}}
          </b-button>
          <b-button v-else variant="primary" size="sm" @click="emitNext()" :disabled="!viewName.trim() || !isNameUnique">
            {{ $t('Webportal.Continue')}}
          </b-button>
        </div>
      </div>
    </template>

    <template #default>
      <BOverlay :show="!isClientReady || !isReady" variant="transparent" opacity="1" blur="2px">
        <template #overlay>
          <default-spinner />
        </template>


        <BFormFloatingLabel :label="$t('Webportal.ModalCreate.Name')" class="mt-3">
          <BFormInput
            v-model="viewName"
            type="text"
            autocomplete="off"
            maxlength="75"
            :state="viewNameDirty && !viewName.trim() ? false : viewName.trim() ? (isNameUnique ? true : false) : null"
            @blur="viewNameDirty = true"
          />
          <BFormInvalidFeedback :state="false" v-if="viewNameDirty && !viewName.trim()">
            {{ $t('Webportal.ModalCreate.Name.Necessary')}}
          </BFormInvalidFeedback>
          <BFormInvalidFeedback :state="false" v-else-if="viewName.trim() && !isNameUnique">
            {{ $t('Webportal.ModalCreate.Name.NotUnique')}}
          </BFormInvalidFeedback>
          <BFormValidFeedback :state="true" v-if="viewName.trim() && isNameUnique">
            {{ $t('Webportal.NameAvailable') }}. ({{ viewName.trim().length }}/75 {{ $t('Webportal.Chars') }})
          </BFormValidFeedback>
        </BFormFloatingLabel>

        <BFormFloatingLabel :label="$t('Webportal.ModalCreate.Description')" class="mt-3">
          <BFormInput v-model="viewDescription"
                      type="text"
                      autocomplete="off"
                      maxlength="75"
                      :state="viewDescription.trim() ? true : null" />
          <BFormValidFeedback :state="true" v-if="viewDescription.trim()">
            ({{ viewDescription.trim().length }}/75 {{ $t('Webportal.Chars') }})
          </BFormValidFeedback>
        </BFormFloatingLabel>

        <BFormCheckbox v-if="!editMode" v-model="copyCurrentLayout" class="mt-3">
          {{ $t('Webportal.ModalCreate.CopyCurrentLayout') }}
        </BFormCheckbox>

        <BFormCheckbox v-if="isMSMUser && !editMode" v-model="isGeneric" class="mt-2">
          {{ $t('Webportal.ModalCreate.GenericView') }}
        </BFormCheckbox>

      </BOverlay>
    </template>


  </b-modal>
</template>
<script>
import { mapGetters } from 'vuex';
import DefaultSpinner from '@/componentsv3/DefaultSpinner.vue';
import { X, Save, ArrowRight } from 'lucide-vue-next';
import UiDataService from '@/services/UiDataService';

export default {
  name: 'ModalCreateCustomView',

  emits: ['next'],

  components: {
    DefaultSpinner,
    X,
    Save,
    ArrowRight
  },

  data() {
    return {
      editMode: false,
      isOpen: false,
      isReady: true,
      boardSetup: null,
      viewName: '',
      viewNameDirty: false,
      viewDescription: '',
      copyCurrentLayout: false,
      isGeneric: false,
      resolvePromise: null,
      rejectPromise: null
    };
  },

  computed: {
    ...mapGetters('clientData', ['isClientReady']),
    ...mapGetters('auth', ['isMSMUser', 'clientId']),
    ...mapGetters('localization', ['currentLocale']),

    isNameUnique() {
      if (!this.viewName.trim() || !this.boardSetup?.views) {
        return true;
      }
      const normalizedName = this.viewName.trim().toLowerCase();
      if (this.editMode && this.boardSetup.label.toLowerCase() === normalizedName) {
        return true;
      }
      return !this.boardSetup.views.some(v => v.label.toLowerCase() === normalizedName);
    }
  },

  methods: {
    open(boardSetup, editMode = false) {
      this.boardSetup = boardSetup;
      this.editMode = editMode;
      this.viewName = editMode ? boardSetup.label : '';
      this.viewNameDirty = false;
      this.viewDescription = editMode ? boardSetup.description : '';
      this.copyCurrentLayout = editMode ? true : false;
      this.isGeneric = editMode ? boardSetup.isGeneric : false;
      this.isOpen = true;

      // Promise zurückgeben, auf die gewartet werden kann
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },

    async handleOk() {
      if (!this.viewName.trim() || !this.isNameUnique) {
        return;
      }

      // Nur für Edit-Modus: PATCH direkt hier ausführen, dann Dialog schließen
      if (this.editMode) {
        const boardSettings = {
          name: this.viewName.trim(),
          description: this.viewDescription.trim(),
          copyCurrentLayout: this.copyCurrentLayout,
          sourceViewId: this.boardSetup.id,
          isGeneric: this.isGeneric,
          isActive: true,
          boardSetup: this.boardSetup
        };

        this.isReady = false;
        try {
          const response = await UiDataService.patchView(
            this.clientId,
            this.currentLocale,
            boardSettings
          );

          if (this.resolvePromise) {
            this.resolvePromise(response);
            this.resolvePromise = null;
            this.rejectPromise = null;
          }

        } catch (error) {
          log.error('Error updating view:', error);
        } finally {
          setTimeout(() => {
            this.isReady = true;
            this.isOpen = false;
          }, 2500);
        }
      }
    },

    emitNext() {
      if (!this.viewName.trim() || !this.isNameUnique) {
        return;
      }

      // Für Create-Modus: Next Event emittieren statt Promise resolven
      const boardSettings = {
        name: this.viewName.trim(),
        description: this.viewDescription.trim(),
        copyCurrentLayout: this.copyCurrentLayout,
        sourceViewId: this.boardSetup.id,
        isGeneric: this.isGeneric,
        isActive: true,
        page: 'overview'
      };

      this.$emit('next', { boardSettings, boardSetup: this.boardSetup });
      this.isOpen = false;
    },

    handleCancel() {
      this.isOpen = false;

      if (this.rejectPromise) {
        this.rejectPromise(new Error('cancelled'));
        this.resolvePromise = null;
        this.rejectPromise = null;
      }
    }
  }
};
</script>

